import { useEffect, useRef, useState } from "react";
import { CalendarLtr20Regular, ChevronDown20Regular } from "@fluentui/react-icons";
import { addDays, toIsoDate } from "../../tarefas/taskDates";

interface PaymentDateMenuProps {
  /** Faturas da seleção que ainda se podem combinar (por pagar, não NC). */
  count: number;
  /** Alguma delas já tem data combinada — mostra "Voltar ao pagamento normal". */
  hasPlanned: boolean;
  busy: boolean;
  disabled: boolean;
  /** Dia combinado (YYYY-MM-DD) ou null para voltar à regra da casa. */
  onPick: (isoDate: string | null) => void;
}

const QUICK_DAYS = [15, 20, 30];

const shortDate = (date: Date) =>
  date.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });

/**
 * "Pagar noutra data": o pagamento combinado com o fornecedor, em dois cliques — abrir e escolher um
 * atalho. Cria a tarefa "Pagar …" nas Tarefas, que se fecha sozinha quando o banco mostrar o
 * pagamento.
 */
export function PaymentDateMenu({ count, hasPlanned, busy, disabled, onPick }: PaymentDateMenuProps) {
  const [open, setOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    firstOptionRef.current?.focus();
    const closeOnOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  const pick = (isoDate: string | null) => {
    setOpen(false);
    setCustomDate("");
    triggerRef.current?.focus();
    onPick(isoDate);
  };

  const today = new Date();
  const optionClass =
    "w-full px-3 py-2 text-left text-sm hover:bg-accent text-foreground flex items-center justify-between gap-3";

  return (
    <div
      className="relative"
      ref={containerRef}
      onBlur={(event) => {
        if (open && !containerRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          // Ocupado mas focável: com disabled o foco caía para o início da página enquanto gravava.
          if (!busy) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-disabled={busy}
        className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50 flex items-center gap-1"
        style={{ borderRadius: "var(--radius-button)" }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarLtr20Regular className="w-4 h-4" />
        {busy ? "A combinar…" : `Pagar noutra data (${count})`}
        <ChevronDown20Regular className="w-4 h-4 text-secondary-text" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Data de pagamento combinada"
          className="absolute left-0 mt-1 bg-card border border-border shadow-lg z-50 py-1"
          style={{ borderRadius: "var(--radius-card)", minWidth: "16rem" }}
        >
          <p className="px-3 pt-1 pb-2 text-xs text-secondary-text">
            Combinado com o fornecedor. Cria a tarefa "Pagar…", que fica concluída quando o pagamento
            aparecer no banco.
          </p>
          {QUICK_DAYS.map((days, index) => {
            const date = addDays(today, days);
            return (
              <button
                key={days}
                ref={index === 0 ? firstOptionRef : undefined}
                type="button"
                className={optionClass}
                onClick={() => pick(toIsoDate(date))}
              >
                <span>Daqui a {days} dias</span>
                <span className="text-secondary-text" style={{ fontVariantNumeric: "tabular-nums" }}>{shortDate(date)}</span>
              </button>
            );
          })}
          <form
            className="px-3 py-2 flex items-center gap-2 border-t border-border mt-1"
            onSubmit={(event) => {
              event.preventDefault();
              if (customDate) pick(customDate);
            }}
          >
            <input
              type="date"
              aria-label="Outro dia"
              value={customDate}
              min={toIsoDate(today)}
              onChange={(event) => setCustomDate(event.target.value)}
              className="flex-1 px-2 py-1 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius-button)" }}
            />
            <button
              type="submit"
              disabled={!customDate}
              className="px-3 py-1 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Aplicar
            </button>
          </form>
          {hasPlanned && (
            <button
              type="button"
              className={`${optionClass} border-t border-border`}
              onClick={() => pick(null)}
            >
              Voltar ao pagamento normal
            </button>
          )}
        </div>
      )}
    </div>
  );
}
